import {Organization} from "./organization";

// 'producer' is a production company signing off its own broadcast
// evaluations. Which company it is comes from `channelId` — the StayLive
// channel number, which unlike the name never changes.
//
// 'evaluationAdmin' runs that same workflow from the association's side and
// nothing else: everything an admin can do inside `/evaluation`, and no way
// into `/admin` — users, licences, leagues, uploads, server params. It exists
// because settling broadcasts is the one job that gets handed to somebody who
// has no business holding the keys to the platform, and until now the only way
// to give it to them was to make them an admin.
export declare type Role = 'admin' | 'owner' | 'operator' | 'evaluationAdmin' | 'producer';

export interface User {
  uid: string,
  email: string,
  displayName?: string,
  phoneNumber?: string,
  photoURL?: string,
  /**
   * The primary role, and the only thing the broadcast side of the app has
   * ever read. Derived from `roles` — see `primaryRole` — and kept in step
   * with it, so an account that gains a second role does not lose what it
   * could already do.
   */
  role?: Role,
  /**
   * Every role the account holds. A person can run broadcasts *and* sign off
   * their club's evaluations, and making that an either/or would have taken
   * the broadcasts away the moment somebody was made a producer.
   *
   * Absent on accounts created before this existed; read it through
   * `effectiveRoles`, never directly.
   */
  roles?: Role[],
  ownerUid?: string,
  organization?: Organization,
  linkedBroadcastUid?: string,
  /** Only meaningful with the `producer` role; cleared when it is taken away. */
  channelId?: number,
}

/**
 * Which role wins when one has to stand for the account.
 *
 * `producer` is deliberately last: it grants nothing on the broadcast side, so
 * an owner who also signs off evaluations must still read as an owner to every
 * screen that asks the old question. `evaluationAdmin` sits just above it for
 * exactly the same reason — it grants nothing outside `/evaluation`, and it
 * must never become the primary `role` of an account that also owns
 * broadcasts, because that is the field the older screens and the older half
 * of `firestore.rules` still read.
 */
export const ROLE_PRECEDENCE: Role[] = ['admin', 'owner', 'operator', 'evaluationAdmin', 'producer'];

/**
 * Who runs the evaluation workflow from the association's side.
 *
 * Every `hasRole(user, 'admin')` inside `/evaluation` asks this instead, and
 * every one outside it does not — that difference is the whole of the new
 * role. Keep it as one named helper rather than an inline pair: the Firestore
 * and Storage rules repeat it in their own language (`isEvaluationAdmin()`),
 * and a check that drifts apart from them fails silently.
 */
export const EVALUATION_ROLES: Role[] = ['admin', 'evaluationAdmin'];

export function isEvaluationAdmin(user: User | null | undefined): boolean {
  return hasAnyRole(user, EVALUATION_ROLES);
}

/** Every role the account holds, including accounts that predate `roles`. */
export function effectiveRoles(user: User | null | undefined): Role[] {
  if (!user) {
    return [];
  }
  if (user.roles?.length) {
    return user.roles;
  }
  return user.role ? [user.role] : [];
}

export function hasRole(user: User | null | undefined, role: Role): boolean {
  return effectiveRoles(user).includes(role);
}

export function hasAnyRole(user: User | null | undefined, roles: Role[]): boolean {
  return roles.some(role => hasRole(user, role));
}

/** The role that stands for the account where only one can be named. */
export function primaryRole(roles: Role[]): Role | null {
  return ROLE_PRECEDENCE.find(role => roles.includes(role)) ?? null;
}

/**
 * What to write when an admin changes what somebody may do.
 *
 * Both fields are written together on purpose: `role` is what the broadcast
 * screens and the security rules for the older collections read, `roles` is the
 * truth. The channel goes with the producer role and is cleared without it, so
 * a demoted account cannot keep reading a production's evaluations.
 */
export function roleUpdate(roles: Role[], channelId?: number | null): Partial<User> {
  const ordered = ROLE_PRECEDENCE.filter(role => roles.includes(role));
  return {
    roles: ordered,
    role: primaryRole(ordered),
    channelId: ordered.includes('producer') ? (channelId ?? null) : null,
  } as Partial<User>;
}
